import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { apiErrorMessage } from '@/i18n'
import { confirmModal } from '@/components/ui/modal'
import { api } from '@/lib/api/client'
import { asList } from '@/lib/api/list'
import { appendSignature, buildComposeBody, normalizeSignature, quoteMessage } from '../mailCompose'
import { composeHasContent } from '../mailComposeUtils'
import { collapseToThreadList, getThreadMessages } from '../mailThread'
import { validateNewAttachments } from '../mailAttachments'
import type { ComposeMode, ComposeState, MailFolder, MailGroup, MailGroupColor, MailMessage, MailNavView, MailSettings } from '../mailTypes'
import { useQueryErrorToast } from './useQueryErrorToast'

const FOLDERS: MailFolder[] = ['INBOX', 'SENT', 'DRAFT', 'TRASH', 'SPAM']
const PAGE_SIZE = 50
const COLOR_HEX: Record<MailGroupColor, string> = {
  primary: '#2563eb',
  success: '#16a34a',
  warning: '#d97706',
  info: '#0891b2',
  destructive: '#dc2626',
}

type Row = Record<string, unknown>

function rowsOf(data: unknown): Row[] {
  if (Array.isArray(data)) return data as Row[]
  if (data && typeof data === 'object') {
    const obj = data as Row
    for (const key of ['items', 'data', 'results', 'messages', 'labels', 'templates']) {
      if (Array.isArray(obj[key])) return obj[key] as Row[]
    }
  }
  return asList(data as Row[] | { items?: Row[] })
}
function str(v: unknown, fallback = '') {
  return v == null ? fallback : String(v)
}
function num(v: unknown) {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}
function snippetOf(html: string) {
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 140)
}
function folderOf(v: unknown): MailFolder {
  const s = String(v ?? 'INBOX').toUpperCase()
  return (FOLDERS as string[]).includes(s) ? (s as MailFolder) : 'INBOX'
}
function colorOf(v: unknown): MailGroupColor {
  const s = String(v ?? '')
  const hit = (Object.entries(COLOR_HEX) as [MailGroupColor, string][]).find(([, hex]) => hex.toLowerCase() === s.toLowerCase())
  return hit?.[0] ?? 'primary'
}
function addrList(v: unknown): string[] {
  if (Array.isArray(v)) return v.map(String)
  if (typeof v === 'string' && v.trim()) return v.split(/[,;]/).map((s) => s.trim()).filter(Boolean)
  return []
}
function mapMessage(row: Row): MailMessage {
  const body = str(row.body_html ?? row.body)
  const labels = Array.isArray(row.label_ids) ? row.label_ids.map(String) : Array.isArray(row.labels) ? (row.labels as Row[]).map((l) => str(l.id ?? l)) : []
  return {
    id: str(row.id),
    thread_id: str(row.thread_id ?? row.id),
    folder: folderOf(row.folder),
    subject: str(row.subject),
    from: str(row.from ?? row.from_email),
    from_name: str(row.from_name) || undefined,
    to: addrList(row.to),
    cc: addrList(row.cc),
    bcc: addrList(row.bcc),
    snippet: str(row.snippet) || snippetOf(body),
    body_html: body,
    is_read: row.is_read !== false && row.unread !== true,
    is_favorite: row.is_favorite === true || row.starred === true,
    group_ids: labels,
    created_at: str(row.created_at ?? row.received_at ?? row.date),
    attachments: Array.isArray(row.attachments)
      ? (row.attachments as Row[]).map((a) => ({
        id: str(a.id),
        name: str(a.file_name ?? a.name ?? a.filename),
        size: num(a.file_size ?? a.size),
        mime: str(a.content_type ?? a.mime ?? a.type),
        downloadable: a.downloadable === true,
      }))
      : [],
  }
}
function mapGroup(row: Row): MailGroup {
  return { id: str(row.id), name: str(row.name), colorToken: colorOf(row.color) }
}

const emptyCompose = (): ComposeState => ({ open: false, session: '', mode: 'new', to: '', cc: '', bcc: '', subject: '', body: '', attachments: [] })

/** Her yeni yazma penceresi için benzersiz oturum (pencere anahtarı). */
const newSession = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`

function matchesView(m: MailMessage, view: MailNavView): boolean {
  if (view.kind === 'favorites') return m.is_favorite && m.folder !== 'TRASH' && m.folder !== 'SPAM'
  if (view.kind === 'group') return m.group_ids.includes(view.groupId) && m.folder !== 'TRASH' && m.folder !== 'SPAM'
  return m.folder === view.folder
}

export function useMailBox() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [view, setView] = useState<MailNavView>({ kind: 'folder', folder: 'INBOX' })
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [compose, setCompose] = useState<ComposeState>(emptyCompose)
  const [unreadOnly, setUnreadOnly] = useState(false)
  const [patches, setPatches] = useState<Record<string, Partial<MailMessage>>>({})
  const [hidden, setHidden] = useState<string[]>([])
  const noSubject = t('mail.mailbox.noSubject')
  const replyPrefix = t('mail.mailbox.replyPrefix')
  const forwardPrefix = t('mail.mailbox.forwardPrefix')

  const folder = view.kind === 'folder' ? view.folder : undefined
  const viewKey = view.kind === 'folder' ? view.folder : view.kind === 'group' ? `group:${view.groupId}` : 'favorites'
  // Liste sunucuda sayfalanır; yıldızlı ve grup görünümleri klasörden bağımsız sunucu filtresidir.
  const pages = useInfiniteQuery({
    queryKey: ['mail', 'messages', viewKey, search, unreadOnly],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const res = await api.get<unknown>('/api/v1/mail/messages', {
        folder,
        favorite: view.kind === 'favorites' || undefined,
        label: view.kind === 'group' ? view.groupId : undefined,
        q: search || undefined,
        unread: unreadOnly || undefined,
        page: pageParam,
        per_page: PAGE_SIZE,
      })
      const meta = res && typeof res === 'object' && !Array.isArray(res) ? ((res as Row).meta as Row | undefined) : undefined
      return { items: rowsOf(res).map(mapMessage), hasNext: meta?.has_next === true, page: pageParam }
    },
    getNextPageParam: (last) => (last.hasNext ? last.page + 1 : undefined),
  })
  const list = { data: useMemo(() => pages.data?.pages.flatMap((p) => p.items), [pages.data]) }
  const detail = useQuery({
    queryKey: ['mail', 'message', selectedId],
    queryFn: async () => mapMessage((await api.get<Row>(`/api/v1/mail/messages/${selectedId}`)) ?? {}),
    enabled: !!selectedId,
  })
  const labels = useQuery({ queryKey: ['mail', 'labels'], queryFn: async () => rowsOf(await api.get<unknown>('/api/v1/mail/labels')).map(mapGroup) })
  const stats = useQuery({ queryKey: ['mail', 'folder-stats'], queryFn: () => api.get<unknown>('/api/v1/mail/folder-stats') })
  const credentials = useQuery({ queryKey: ['mail', 'credentials'], queryFn: () => api.get<Row>('/api/v1/mail/credentials') })

  useQueryErrorToast([
    labels,
    stats,
    credentials,
    detail,
  ])

  const groups = labels.data ?? []
  const rawSignature = str(credentials.data?.signature_html)
  const signatureHtml = useMemo(() => normalizeSignature(rawSignature), [rawSignature])
  const settings: MailSettings = { signature_html: signatureHtml }
  const fromAddress = str(credentials.data?.email ?? credentials.data?.address, 'destek@urunver.com')

  const messages = useMemo(() => {
    const hiddenSet = new Set(hidden)
    const base = (list.data ?? []).filter((m) => !hiddenSet.has(m.id)).map((m) => ({ ...m, ...patches[m.id] }))
    if (detail.data && !hiddenSet.has(detail.data.id)) {
      const i = base.findIndex((m) => m.id === detail.data.id)
      const merged = { ...detail.data, ...patches[detail.data.id] }
      if (i >= 0) base[i] = { ...base[i], ...merged }
      else base.unshift(merged)
    }
    return base
  }, [list.data, detail.data, patches, hidden])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const matched = messages
      .filter((m) => matchesView(m, view))
      .filter((m) => !unreadOnly || !m.is_read)
      .filter((m) => !q || m.subject.toLowerCase().includes(q) || m.from.toLowerCase().includes(q) || (m.from_name?.toLowerCase().includes(q) ?? false) || m.snippet.toLowerCase().includes(q))
    return collapseToThreadList(matched)
  }, [messages, view, search, unreadOnly])

  const selected = useMemo(() => (selectedId ? (messages.find((m) => m.id === selectedId) ?? null) : null), [messages, selectedId])
  const threadMessages = useMemo(() => (selected ? getThreadMessages(messages, selected) : []), [messages, selected])

  const counts = useMemo(() => {
    const raw = stats.data
    const byFolder = new Map<string, { total: number; unread: number }>()
    const source = raw && typeof raw === 'object' && !Array.isArray(raw) && (raw as Row).folders ? (raw as Row).folders : raw
    for (const row of rowsOf(source)) {
      byFolder.set(String(row.folder ?? '').toUpperCase(), { total: num(row.total ?? row.count), unread: num(row.unread) })
    }
    if (raw && typeof raw === 'object' && !Array.isArray(raw) && !Array.isArray((raw as Row).folders)) {
      for (const [key, val] of Object.entries(raw as Row)) {
        if (val && typeof val === 'object') byFolder.set(key.toUpperCase(), { total: num((val as Row).total ?? (val as Row).count), unread: num((val as Row).unread) })
      }
    }
    const countFolder = (folderName: MailFolder) => byFolder.get(folderName)?.total ?? messages.filter((m) => m.folder === folderName).length
    return {
      inboxUnread: byFolder.get('INBOX')?.unread ?? messages.filter((m) => m.folder === 'INBOX' && !m.is_read).length,
      favorites: messages.filter((m) => m.is_favorite && m.folder !== 'TRASH' && m.folder !== 'SPAM').length,
      drafts: countFolder('DRAFT'),
      spam: countFolder('SPAM'),
      trash: countFolder('TRASH'),
      sent: countFolder('SENT'),
      byGroup: Object.fromEntries(groups.map((g) => [g.id, messages.filter((m) => m.group_ids.includes(g.id) && m.folder !== 'TRASH' && m.folder !== 'SPAM').length])) as Record<string, number>,
    }
  }, [stats.data, messages, groups])

  const composeRef = useRef(compose)
  const autosaveTimer = useRef<number | undefined>(undefined)
  useEffect(() => {
    composeRef.current = compose
  }, [compose])

  function patchMessage(id: string, patch: Partial<MailMessage>) {
    setPatches((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }))
  }
  function patchMany(ids: Iterable<string>, patch: Partial<MailMessage>) {
    setPatches((prev) => {
      const next = { ...prev }
      for (const id of ids) next[id] = { ...next[id], ...patch }
      return next
    })
  }
  function dropPatches(ids: string[]) {
    setPatches((prev) => {
      const next = { ...prev }
      for (const id of ids) delete next[id]
      return next
    })
  }
  function invalidate() {
    return qc.invalidateQueries({ queryKey: ['mail'] })
  }
  /**
   * İşlem ekranda hemen görünür, sonra sunucuya yazılır (sunucu Gmail'e de iletir). Liste yenilenince geçici
   * yamalar düşer; hata olursa geri alınır.
   */
  async function act(ids: string[], action: 'read' | 'unread' | 'star' | 'unstar' | 'move' | 'delete', folderName?: MailFolder) {
    if (ids.length === 0) return
    const path = action === 'move' || action === 'delete' ? '/api/v1/mail/messages/actions/folder' : '/api/v1/mail/messages/actions/status'
    try {
      await api.post(path, { ids, action, folder: folderName })
      await invalidate()
    } catch (e) {
      if (action === 'delete') setHidden((h) => h.filter((id) => !ids.includes(id)))
      toast.error(apiErrorMessage(e))
    } finally {
      dropPatches(ids)
    }
  }
  function threadIds(ids: string[]) {
    const targets = new Set<string>()
    for (const id of ids) {
      const msg = messages.find((m) => m.id === id)
      if (!msg) continue
      for (const m of getThreadMessages(messages, msg)) targets.add(m.id)
    }
    return [...targets]
  }

  function selectView(next: MailNavView) {
    setView(next)
    setSelectedId(null)
  }
  function selectMessage(id: string) {
    setSelectedId(id)
    const msg = messages.find((m) => m.id === id)
    if (!msg) return
    const unread = getThreadMessages(messages, msg).filter((m) => !m.is_read).map((m) => m.id)
    if (unread.length === 0) return
    patchMany(unread, { is_read: true })
    void act(unread, 'read')
  }
  function toggleFavorite(id: string) {
    const msg = messages.find((m) => m.id === id)
    if (!msg) return
    patchMessage(id, { is_favorite: !msg.is_favorite })
    void act([id], msg.is_favorite ? 'unstar' : 'star')
  }
  /** Çöpe/spama taşıma ve kalıcı silme her giriş noktasında (liste, detay, toplu, kısayol) aynı onayı ister. */
  function confirmDestructive(kind: 'mailTrash' | 'mailSpam' | 'mailDeleteForever', count: number) {
    return confirmModal({
      title: t(`confirm.${kind}.title`, { count }),
      description: t(`confirm.${kind}.body`, { count }),
      confirmLabel: t(`confirm.${kind}.confirm`),
      destructive: kind !== 'mailSpam',
    })
  }
  function moveToFolder(id: string, next: MailFolder) {
    return moveMany([id], next)
  }
  function setRead(ids: string[], read: boolean) {
    const targets = threadIds(ids)
    patchMany(targets, { is_read: read })
    if (!read && selectedId && targets.includes(selectedId)) setSelectedId(null)
    void act(targets, read ? 'read' : 'unread')
  }
  /** Vazgeçilirse false döner (çağıran seçimi korur). */
  async function moveMany(ids: string[], next: MailFolder) {
    if (ids.length === 0) return false
    if (next === 'TRASH' || next === 'SPAM') {
      if (!(await confirmDestructive(next === 'TRASH' ? 'mailTrash' : 'mailSpam', ids.length))) return false
    }
    patchMany(ids, { folder: next })
    if (selectedId && ids.includes(selectedId)) setSelectedId(null)
    void act(ids, 'move', next)
    return true
  }
  function setFavoriteMany(ids: string[], favorite: boolean) {
    patchMany(ids, { is_favorite: favorite })
    void act(ids, favorite ? 'star' : 'unstar')
  }
  function closeMessage() {
    setSelectedId(null)
  }
  async function deletePermanentMany(ids: string[]) {
    if (ids.length === 0 || !(await confirmDestructive('mailDeleteForever', ids.length))) return false
    setHidden((hidden) => [...hidden, ...ids])
    if (selectedId && ids.includes(selectedId)) setSelectedId(null)
    void act(ids, 'delete')
    return true
  }
  function deletePermanent(id: string) {
    return deletePermanentMany([id])
  }
  async function toggleGroupOnMessage(messageId: string, groupId: string) {
    const msg = messages.find((m) => m.id === messageId)
    if (!msg) return
    const has = msg.group_ids.includes(groupId)
    patchMessage(messageId, { group_ids: has ? msg.group_ids.filter((g) => g !== groupId) : [...msg.group_ids, groupId] })
    if (has) await api.del(`/api/v1/mail/messages/${messageId}/labels/${groupId}`)
    else await api.post(`/api/v1/mail/messages/${messageId}/labels/${groupId}`, {})
    invalidate()
  }
  async function addMessageToGroup(messageId: string, groupId: string) {
    const msg = messages.find((m) => m.id === messageId)
    if (!msg || msg.group_ids.includes(groupId)) return
    patchMessage(messageId, { group_ids: [...msg.group_ids, groupId] })
    await api.post(`/api/v1/mail/messages/${messageId}/labels/${groupId}`, {})
    toast.success(t('mail.mailbox.groups.added'))
    invalidate()
  }
  async function addGroup(name: string, colorToken: MailGroupColor = 'primary') {
    const trimmed = name.trim()
    if (!trimmed) return
    await api.post('/api/v1/mail/labels', { name: trimmed, color: COLOR_HEX[colorToken] })
    invalidate()
  }
  async function renameGroup(id: string, name: string) {
    const next = name.trim()
    if (!next) return
    const current = groups.find((g) => g.id === id)
    await api.put(`/api/v1/mail/labels/${id}`, { name: next, color: COLOR_HEX[current?.colorToken ?? 'primary'] })
    invalidate()
  }
  async function deleteGroup(id: string) {
    await api.del(`/api/v1/mail/labels/${id}`)
    if (view.kind === 'group' && view.groupId === id) setView({ kind: 'folder', folder: 'INBOX' })
    invalidate()
  }
  async function saveSettings(next: MailSettings) {
    await api.put('/api/v1/mail/signature', { signature_html: next.signature_html })
    invalidate()
  }

  function openCompose(mode: ComposeMode, source?: MailMessage) {
    const { signature_html } = settings
    if (mode === 'draft' && source) {
      setCompose({
        open: true,
        session: newSession(),
        mode: 'draft',
        draftId: source.id,
        to: source.to.join(', '),
        cc: (source.cc ?? []).join(', '),
        bcc: (source.bcc ?? []).join(', '),
        subject: source.subject,
        body: appendSignature(source.body_html, signature_html),
        replyToId: source.id,
        attachments: [...(source.attachments ?? [])],
      })
      return
    }
    if ((mode === 'reply' || mode === 'replyAll' || mode === 'forward') && source) {
      const quoted = quoteMessage(source.from_name ? `${source.from_name} <${source.from}>` : source.from, source.created_at, source.body_html)
      const to = mode === 'forward' ? '' : mode === 'replyAll' ? [source.from, ...source.to.filter((a) => a !== fromAddress)].join(', ') : source.from
      const prefix = mode === 'forward' ? forwardPrefix : replyPrefix
      setCompose({
        open: true,
        session: newSession(),
        mode,
        to,
        cc: mode === 'replyAll' ? (source.cc ?? []).join(', ') : '',
        bcc: mode === 'replyAll' ? (source.bcc ?? []).join(', ') : '',
        subject: source.subject.startsWith(prefix) ? source.subject : `${prefix}${source.subject}`,
        body: buildComposeBody(signature_html, quoted),
        replyToId: source.id,
        attachments: mode === 'forward' ? [...(source.attachments ?? [])] : [],
      })
      return
    }
    setCompose({ open: true, session: newSession(), mode: 'new', to: '', cc: '', bcc: '', subject: '', body: buildComposeBody(signature_html), attachments: [] })
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null
      const tag = el?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || el?.isContentEditable) return
      if (e.key === 'c' || e.key === 'C') {
        e.preventDefault()
        setCompose((c) => (c.open ? c : { open: true, session: newSession(), mode: 'new', to: '', cc: '', bcc: '', subject: '', body: buildComposeBody(settings.signature_html), attachments: [] }))
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [settings.signature_html])

  function parseAddresses(raw: string) {
    return raw.split(/[,;]/).map((s) => s.trim()).filter(Boolean)
  }
  function draftBody(source: ComposeState) {
    return {
      to: parseAddresses(source.to),
      cc: parseAddresses(source.cc),
      bcc: parseAddresses(source.bcc),
      subject: source.subject || noSubject,
      body_text: snippetOf(source.body),
      body_html: appendSignature(source.body, settings.signature_html),
    }
  }
  async function persistComposeDraft(closeAfter: boolean, source = composeRef.current) {
    if (!source.open || !composeHasContent(source)) {
      if (closeAfter) setCompose(emptyCompose())
      return
    }
    const body = draftBody(source)
    if (source.draftId) await api.put(`/api/v1/mail/drafts/${source.draftId}`, body)
    else {
      const created = await api.post<Row>('/api/v1/mail/drafts', body)
      const id = str(created.id)
      if (!closeAfter && id) setCompose((c) => ({ ...c, draftId: id, mode: 'draft' }))
    }
    if (closeAfter) setCompose(emptyCompose())
    invalidate()
  }
  function closeCompose() {
    if (composeHasContent(compose)) {
      void persistComposeDraft(true).then(() => toast.info(t('mail.mailbox.draftAutoSaved')))
    } else setCompose(emptyCompose())
  }
  function patchCompose(patch: Partial<ComposeState>) {
    setCompose((c) => ({ ...c, ...patch }))
    window.clearTimeout(autosaveTimer.current)
    autosaveTimer.current = window.setTimeout(() => {
      void persistComposeDraft(false)
    }, 2500)
  }
  function saveDraft() {
    void persistComposeDraft(true)
  }
  async function sendMail() {
    const to = parseAddresses(compose.to)
    if (to.length === 0) return false
    const body = draftBody(compose)
    if (compose.draftId) {
      await api.put(`/api/v1/mail/drafts/${compose.draftId}`, body)
      await api.post(`/api/v1/mail/drafts/${compose.draftId}/send`, {})
    } else {
      const created = await api.post<Row>('/api/v1/mail/drafts', body)
      const id = str(created.id)
      if (id) await api.post(`/api/v1/mail/drafts/${id}/send`, {})
      else await api.post('/api/v1/mail/send', { to, subject: body.subject, body_html: body.body_html })
    }
    setCompose(emptyCompose())
    invalidate()
    // Gönderim worker'da birkaç saniye sürer; asıl tazeleme mail.message.sent sinyaliyle gelir, bu yalnızca yedek.
    window.setTimeout(() => void invalidate(), 5000)
    return true
  }
  function addAttachments(files: FileList | File[]) {
    const fileList = Array.from(files)
    if (fileList.length === 0) return
    const result = validateNewAttachments(compose.attachments ?? [], fileList)
    if (result.error === 'type') toast.error(t('mail.mailbox.attachments.typeError', { name: result.rejectedName }))
    else if (result.error === 'totalSize') toast.error(t('mail.mailbox.attachments.totalSizeError'))
    if (result.ok.length === 0) return
    patchCompose({ attachments: [...(compose.attachments ?? []), ...result.ok] })
  }
  function removeAttachment(id: string) {
    patchCompose({ attachments: (compose.attachments ?? []).filter((a) => a.id !== id) })
  }

  return {
    view,
    selectView,
    loading: pages.isLoading,
    hasMore: !!pages.hasNextPage,
    loadingMore: pages.isFetchingNextPage,
    loadMore: () => void pages.fetchNextPage(),
    search,
    setSearch,
    unreadOnly,
    setUnreadOnly,
    filtered,
    selected,
    threadMessages,
    selectedId,
    selectMessage,
    closeMessage,
    setRead,
    moveMany,
    setFavoriteMany,
    groups,
    settings,
    counts,
    messages,
    compose,
    openCompose,
    closeCompose,
    patchCompose,
    saveDraft,
    sendMail,
    addAttachments,
    removeAttachment,
    toggleFavorite,
    moveToFolder,
    deletePermanent,
    deletePermanentMany,
    toggleGroupOnMessage,
    addMessageToGroup,
    addGroup,
    renameGroup,
    deleteGroup,
    saveSettings,
  }
}

export type MailBoxApi = ReturnType<typeof useMailBox>
